import { memo, useCallback, useMemo, useState } from 'react';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ListActionBar from 'src/components/ListActionBar';
import TableContainer from 'src/components/Table/TableContainer';
import LoadingRows from 'src/components/Table/LoadingRows';
import NoDataCell from 'src/components/NoDataCell';
import TableHeadTypography from 'src/components/TableHeadTypography';
import Iconify from 'src/components/Iconify';
import useTypographyMediaQuery from 'src/hooks/useTypographyMediaQuery';
import { readFilterParams, useFilterParams } from 'src/hooks/filterParams';
import { useInfiniteDevicePolicies } from 'src/hooks/wdacPolicy';
import { useDeviceDetails } from 'src/hooks/device';
import { formatDate } from 'src/utils/formatTime';
import { getBasePolicyModeOptions } from 'src/utils/enums/basePolicyMode';
import { getWdacPolicyTypeOptions } from 'src/utils/enums/wdacPolicyType';
import {
  POLICY_VISIBILITY,
  getPolicyVisibilityOptions,
  toIsHiddenValues,
} from 'src/utils/enums/uiOnly/policyVisibility';
import DevicePolicyDetailsModal from './DevicePolicyDetailsModal';

const PAGE_LIMIT = 50;
const DEFAULT_SORT = { column: 'LastAppliedDate', direction: 'desc' };

// Active-on-device telemetry only comes from the CLI/Service agent, so a LogCollector-only device
// would show nothing under the usual default and should start unfiltered instead.
const getDefaultFilters = lastRuntimeMode => ({
  basePolicyMode: '',
  policyType: '',
  activeOnDevice: lastRuntimeMode === 'LogCollector' ? [] : ['true'],
  visibilities: [POLICY_VISIBILITY.Visible],
});

const POLICY_TABLE_COLUMNS = [
  {
    key: 'policyName',
    label: 'Policy Name',
    sortable: true,
    sortKey: 'PolicyName',
    width: '28%',
  },
  {
    key: 'policyVersion',
    label: 'Policy Version',
    sortable: true,
    sortKey: 'PolicyVersion',
    width: '18%',
  },
  {
    key: 'policyType',
    label: 'Policy Type',
    sortable: true,
    sortKey: 'PolicyType',
    width: '18%',
  },
  {
    key: 'basePolicyMode',
    label: 'Base Policy Mode',
    sortable: true,
    sortKey: 'BasePolicyMode',
    width: '16%',
  },
  {
    key: 'lastAppliedDate',
    label: 'Last Applied Date',
    sortable: true,
    sortKey: 'LastAppliedDate',
    width: '20%',
  },
];

const PolicyTableContent = ({ deviceId, lastRuntimeMode }) => {
  const { mediumScreen, largeScreen, smallScreen } = useTypographyMediaQuery();

  const defaultFilters = getDefaultFilters(lastRuntimeMode);
  const [searchValue, setSearchValue] = useState('');
  const [filters, setFilters] = useState(() => readFilterParams(defaultFilters) ?? defaultFilters);
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [detailsModal, setDetailsModal] = useState({ open: false, row: null });

  useFilterParams(filters);
  const trimmedSearchValue = useMemo(() => searchValue.trim(), [searchValue]);

  const apiQuery = useMemo(
    () => ({
      ...(trimmedSearchValue ? { search: trimmedSearchValue } : {}),
      ...(filters.basePolicyMode ? { basePolicyMode: filters.basePolicyMode } : {}),
      ...(filters.policyType ? { policyType: filters.policyType } : {}),
      ...(filters.activeOnDevice.length
        ? { activeOnDevice: filters.activeOnDevice.map(value => value === 'true') }
        : {}),
      ...(filters.visibilities.length ? { isHidden: toIsHiddenValues(filters.visibilities) } : {}),
      sortColumn: sort.column,
      sortDesc: sort.direction === 'desc',
    }),
    [filters, sort, trimmedSearchValue],
  );

  const policiesQuery = useInfiniteDevicePolicies({
    deviceId,
    apiQuery,
    limit: PAGE_LIMIT,
  });

  const policies = useMemo(
    () =>
      policiesQuery.data?.pages?.flatMap(page =>
        (page.policies || []).map(({ policy, lastAppliedDate }) => ({
          ...policy,
          lastAppliedDate,
        })),
      ) || [],
    [policiesQuery.data],
  );
  const totalCount = policiesQuery.data?.pages?.[0]?.count ?? 0;

  const loadMore = useCallback(() => {
    if (policiesQuery.hasNextPage) {
      policiesQuery.fetchNextPage();
    }
  }, [policiesQuery]);

  const [sentryRef] = useInfiniteScroll({
    loading: policiesQuery.isFetchingNextPage,
    hasNextPage: policiesQuery.hasNextPage,
    disabled: false,
    onLoadMore: loadMore,
    rootMargin: '0px 0px 400px 0px',
  });

  const handleSort = useCallback(column => {
    if (!column.sortable || !column.sortKey) {
      return;
    }

    setSort(prev =>
      prev.column === column.sortKey
        ? { column: column.sortKey, direction: prev.direction === 'desc' ? 'asc' : 'desc' }
        : { column: column.sortKey, direction: 'desc' },
    );
  }, []);

  const handleFilterChange = useCallback(nextFilters => {
    setFilters({
      basePolicyMode: nextFilters.basePolicyMode || '',
      policyType: nextFilters.policyType || '',
      activeOnDevice: nextFilters.activeOnDevice || [],
      visibilities: nextFilters.visibilities || [],
    });
  }, []);

  const handleRowClick = useCallback(policy => {
    setDetailsModal({ open: true, row: policy });
  }, []);

  const filterOptions = useMemo(
    () => [
      {
        key: 'basePolicyMode',
        type: 'select',
        label: 'Base Policy Mode',
        options: [{ value: '', label: 'All modes' }, ...getBasePolicyModeOptions()],
      },
      {
        key: 'policyType',
        type: 'select',
        label: 'Policy Type',
        options: [{ value: '', label: 'All types' }, ...getWdacPolicyTypeOptions()],
      },
      {
        key: 'activeOnDevice',
        type: 'checkbox',
        label: 'Active On Device',
        options: [
          { value: 'true', label: 'Active' },
          { value: 'false', label: 'Inactive' },
        ],
      },
      {
        key: 'visibilities',
        type: 'checkbox',
        label: 'Visibility',
        options: getPolicyVisibilityOptions(),
      },
    ],
    [],
  );

  const renderCell = useCallback((column, policy) => {
    const value = policy[column.key];

    return (
      <TableCell
        key={`${policy.policyVersionId}-${column.key}`}
        sx={{ width: column.width, py: 1.5, px: 2 }}>
        <Typography
          variant="caption"
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            wordBreak: 'break-word',
          }}>
          {column.key === 'lastAppliedDate' ? formatDate(value) || '--' : value || '--'}
        </Typography>
      </TableCell>
    );
  }, []);

  const PolicyColumnHeader = ({ column }) => (
    <Stack
      direction="row"
      gap={0.5}
      alignItems="center"
      onClick={column.sortable ? () => handleSort(column) : undefined}
      sx={{ cursor: column.sortable ? 'pointer' : 'default' }}>
      <TableHeadTypography
        hasSorting={sort.column === column.sortKey}
        mediumScreen={mediumScreen}
        largeScreen={largeScreen}
        smallScreen={smallScreen}>
        {column.label}
      </TableHeadTypography>
      {column.sortable ? (
        <Iconify
          icon={
            sort.column === column.sortKey
              ? sort.direction === 'asc'
                ? 'mdi:arrow-up'
                : 'mdi:arrow-down'
              : 'mdi:swap-vertical'
          }
          width={14}
          height={14}
          color={sort.column === column.sortKey ? '#7DFF92' : '#9CA3AF'}
        />
      ) : null}
    </Stack>
  );

  return (
    <Box sx={{ position: 'relative', mt: 3 }}>
      <ListActionBar
        onSearch={setSearchValue}
        defaultSearch={searchValue}
        onResetSearch={() => setSearchValue('')}
        searchPlaceholder="Search policies"
        filters={filters}
        onFilterChange={handleFilterChange}
        filterOptions={filterOptions}
        filterChips
        limitChips={2}
        filterButtonCount={totalCount}
      />

      <TableContainer
        roundedTop
        styles={{
          maxHeight: 'calc(100vh - 368px)',
        }}>
        <Table stickyHeader size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
          <colgroup>
            {POLICY_TABLE_COLUMNS.map(column => (
              <col key={column.key} style={{ width: column.width }} />
            ))}
          </colgroup>
          <TableHead sx={{ backgroundColor: '#3F3F3F' }}>
            <TableRow>
              {POLICY_TABLE_COLUMNS.map(column => (
                <TableCell key={column.key} sx={{ p: '8px 16px', verticalAlign: 'middle' }}>
                  <PolicyColumnHeader column={column} />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody sx={{ backgroundColor: '#4B4B4B' }}>
            {policiesQuery.isLoading ? (
              <LoadingRows columnCount={POLICY_TABLE_COLUMNS.length} rowCount={6} />
            ) : (
              <>
                {policies.map((policy, index) => (
                  <TableRow
                    key={policy.policyVersionId}
                    ref={index === policies.length - 1 ? sentryRef : null}
                    hover
                    onClick={() => handleRowClick(policy)}
                    sx={{
                      cursor: 'pointer',
                      '&:nth-of-type(even)': {
                        backgroundColor: '#ffffff14',
                      },
                    }}>
                    {POLICY_TABLE_COLUMNS.map(column => renderCell(column, policy))}
                  </TableRow>
                ))}
                {policiesQuery.isFetchingNextPage ? (
                  <LoadingRows columnCount={POLICY_TABLE_COLUMNS.length} rowCount={3} />
                ) : null}
                {!policiesQuery.isLoading && policies.length === 0 ? (
                  <NoDataCell colSpan={POLICY_TABLE_COLUMNS.length}>No Data</NoDataCell>
                ) : null}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <DevicePolicyDetailsModal
        open={detailsModal.open}
        onClose={() => setDetailsModal({ open: false, row: null })}
        deviceId={deviceId}
        envPolicyVersionId={detailsModal.row?.policyVersionId}
        fallbackRow={detailsModal.row}
      />
    </Box>
  );
};

const PolicyTable = ({ deviceId }) => {
  const deviceQuery = useDeviceDetails({ deviceId });

  if (deviceQuery.isLoading) {
    return (
      <Box sx={{ position: 'relative', mt: 3 }}>
        <TableContainer roundedTop styles={{ maxHeight: 'calc(100vh - 368px)' }}>
          <Table stickyHeader size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
            <TableBody sx={{ backgroundColor: '#4B4B4B' }}>
              <LoadingRows columnCount={POLICY_TABLE_COLUMNS.length} rowCount={6} />
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  }

  return (
    <PolicyTableContent
      deviceId={deviceId}
      lastRuntimeMode={deviceQuery.data?.device?.lastRuntimeMode}
    />
  );
};

export default memo(PolicyTable);
